/**
 * Email Service for sending invitation emails via Resend
 *
 * This service calls the /api/invitations/send-email endpoint
 * which uses Resend to send professional invitation emails.
 */

import { auth } from '../config/firebase';

async function getAuthToken(): Promise<string | null> {
  try {
    return auth.currentUser ? await auth.currentUser.getIdToken() : null;
  } catch {
    return null;
  }
}

export interface SendInvitationEmailParams {
  email: string;
  inviteLink: string;
  type: 'manager' | 'admin' | 'org-admin' | 'student';
  organizationName?: string;
  /**
   * Optional: organization id used by the server-side branding helper
   * (`getOrgBranding`) to fetch org-specific branding for student/manager
   * invitations. Without it, the helper silently falls back to the
   * platform-default branding.
   */
  organizationId?: string;
  /**
   * Optional: display name of the inviting user, rendered in the
   * "Invited by …" attribution card in the invitation email.
   */
  senderName?: string;
  /**
   * Optional: email address of the inviting user, rendered in the
   * "Invited by …" attribution card in the invitation email.
   */
  senderEmail?: string;
}

export interface SendInvitationEmailResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Send an invitation email using Resend
 */
export async function sendInvitationEmail(
  params: SendInvitationEmailParams
): Promise<SendInvitationEmailResponse> {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ||
                       (import.meta.env.DEV ? 'http://localhost:3002' : window.location.origin);

    const token = await getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${apiBaseUrl}/api/invitations/send-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to send email',
      };
    }

    return {
      success: true,
      messageId: data.messageId,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error while sending email',
    };
  }
}

/**
 * Generate a Firebase authentication link using Firebase Admin SDK
 * This returns a complete Firebase email link with oobCode that can be sent via Resend
 */
export interface GenerateAuthLinkParams {
  email: string;
  continueUrl: string;
}

export interface GenerateAuthLinkResponse {
  success: boolean;
  link?: string;
  error?: string;
}

export async function generateFirebaseAuthLink(
  params: GenerateAuthLinkParams
): Promise<GenerateAuthLinkResponse> {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ||
                       (import.meta.env.DEV ? 'http://localhost:3002' : window.location.origin);

    const token = await getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${apiBaseUrl}/api/auth/generate-email-link`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to generate auth link',
      };
    }

    return {
      success: true,
      link: data.link,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Network error while generating auth link',
    };
  }
}
